import React, { useCallback, useEffect, useState } from "react";

import { ChangeEvent, KeyboardEvent } from "react";

export const TextArea = ({
  input = '',
  accessibility,
  className,
  disabled = false,
  error,
  errorText,
  hintText,
  maxLength,
  onBlur,
  onChange,
  onFocus,
  onKeyPress,
  placeHolder,
  labelText: title,
  ...props
}: TextAreaProps) => {
  const [currentText, setCurrentText] = useState(input);
  const [isFocused, setIsFocused] = useState(false);
  const id = props["id"] ? props["id"] : "txtAreaId" + (new Date()).getTime() + Math.random().toString(36).substring(2, 2);

  let descIds = [];
  if (maxLength) {
    descIds.push(id);
  }
  descIds = [];
  if (hintText && error) {
    descIds.push(id);
    descIds.push("-error");
  }

  useEffect(() => {
    setCurrentText(input);
  }, [input]);

  const onChangeCallback = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(event);
      }
      setCurrentText(event.target.value);
    },
    [currentText]
  );

  const onKeyPressCallback = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (onKeyPress) {
        onKeyPress(currentText, event);
      }
      setCurrentText(event.currentTarget.value);
    },
    [currentText]
  );

  const onBlurCallback = useCallback(() => {
    if (onBlur) {
      onBlur(currentText);
    }
    setIsFocused(false);
  }, [currentText]);

  const onFocusCallback = useCallback(
    (event: any) => {
      if (onFocus) {
        onFocus(currentText, event);
      }
      setIsFocused(true);
    },
    [currentText]
  );

  return (
    <>
      <div className={`rel ${className}`}>
        {title && (
          <label
            htmlFor={id}
            className={[
              
              disabled ? "disabled" : "",
              "formfield-label inline-flex",
              error ? "error" : ""
            ].join(" ")}
          >
            {title}
            {hintText && !error && (
              <span className="hidden-spoken">{": " + hintText}</span>
            )}
          </label>
        )}
        {maxLength && (
          <span
            className={"color-gray-500 char-counter pad-b-xxs"}
            aria-live={maxLength - currentText.length <= 20 ? "assertive" : "polite"}
            aria-atomic={false}
            aria-label={currentText.length + " out of " + maxLength + " characters"}
           role="textbox"
          >
            {currentText.length + "/" + maxLength}
          </span>
        )}
        <textarea
          id={id}  
          placeholder={placeHolder}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            descIds.length === 0 ? undefined : descIds.join("")
          }
          maxLength={maxLength}
          value={currentText}
          onChange={onChangeCallback}
          onKeyPress={onKeyPressCallback}
          onBlur={onBlurCallback}
          onFocus={onFocusCallback}
          disabled={disabled}
          aria-label={accessibility}
        />
        {!error && hintText && (
          <div
            className="formfield-msg"
            role={"tooltip"}
            tabIndex={isFocused ? 0 : -1}
          >
            {hintText}
          </div>
        )}
        {error && errorText && (
          <div
            id={id+"-error"}
            className="formfield-msg"
            aria-atomic={true}
            tabIndex={isFocused ? 0 : -1}
            aria-live={isFocused && error ? "assertive" : undefined}
          >
            {errorText}
          </div>
        )}
      </div>
      <style jsx>{`
        .char-counter {
          font-size: 13px;
          float: right;
        }
      `}</style>
    </>
  );
};

export type TextAreaProps = {
  /**
   * Callback to be called on text area blur
   */
  onBlur?: (currentValue: string) => void;
  /**
   * Callback to be called when text area value has changed
   */
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * Callback to be called when text area comes into focus
   */
  onFocus?: (currentValue: string, e: any) => void;
  /**
   * Callback to be called before the value of text area has changed
   */
  onKeyPress?: (
    oldValue: string,
    e: KeyboardEvent<HTMLTextAreaElement>
  ) => void;
  /** Sets the hint text for the TextArea */
  hintText?: string;
  /** Determines if TextArea should be in an error state */
  error?: boolean;
  /** Sets the error text for the TextArea */
  errorText?: string;
  /** Sets value of TextArea */
  input?: string;
  /**
   * Sets the aria-label for the TextArea
   */
  accessibility?: string;
  /**
   * Adds class to the outer-most container
   */
  className?: string;
  /**
   *  Determines if TextArea should be disabled
   */
  disabled?: boolean;
  /**
   * Sets maxLength of TextArea
   */
  maxLength?: number;
  /**
   * Sets the placeholder text for TextArea
   */
  placeHolder?: string;
  /**
   * Adds a label to the text area
   */
  labelText?: string;
  /** Used for unique  identification of element. */
  id?: string;
};
